import React, { useState } from 'react';

const PLAN_HIERARCHY = { professional: 1, business: 2, starter: 1, pro: 1, team: 2 };

const PLANS = [
  {
    id: 'professional',
    name: 'Professional',
    price: '$399',
    period: 'per month',
    setup: '$499',
    setupLabel: 'one-time setup fee',
    description: 'Get CMMC-certified before the November 2026 deadline. Full certification prep for small contractors.',
    priceId: 'price_1TFPHDQbWl9ruvOMCnG1eNne',
    setupPriceId: 'price_1TFPYJQbWl9ruvOMZwRNXvBy',
    color: 'var(--accent)',
    features: [
      'CMMC L1, L2, L3 + NIST 800-171',
      'AI compliance assistant',
      'Policy template library',
      'Auditor access portal',
      'Annual affirmation tracker',
      'SSP & POA&M PDF export',
      'Up to 3 team members',
      '3 frameworks included',
    ],
    popular: false,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$799',
    period: 'per month',
    setup: '$799',
    setupLabel: 'one-time setup fee',
    description: 'The complete compliance platform. Everything you need to hit the November 2026 CMMC deadline and stay audit-ready.',
    priceId: 'price_1TFPHsQbWl9ruvOMPrgSpqw7',
    setupPriceId: 'price_1TFPYzQbWl9ruvOM5lnm4ppq',
    color: 'var(--green)',
    features: [
      'Everything in Professional',
      'All 8 compliance frameworks included',
      'SOC 2 · ISO 27001 · HIPAA · NIST CSF',
      'PCI DSS v4.0 · FedRAMP Moderate',
      'Unlimited team seats',
      'Priority support',
      'Dedicated onboarding call',
      'SSP spreadsheet export',
    ],
    popular: true,
  },
];

const TOTALS = {
  Professional: '$898',
  Business:     '$1,598',
};

const FUNCTION_URL =
  'https://ubtjzzsgcvxrkhqlcrlr.supabase.co/functions/v1/create-checkout-session';

function LogoMark({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="40" height="40" rx="10" fill="url(#pricingLogoGrad)" />
      <path d="M20 6L8 11v10c0 7.18 5.13 13.89 12 15.5C27.87 34.89 33 28.18 33 21V11L20 6z"
        fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.35)" strokeWidth="1" />
      <path d="M14 20.5l4 4 8-8" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <defs>
        <linearGradient id="pricingLogoGrad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop stopColor="#2D7DD2" /><stop offset="1" stopColor="#1A4E8A" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export default function Pricing({ onBack, session, profile, currentPlan = '', onPlanChange }) {
  const userEmail = session?.user?.email;

  const [selected, setSelected]           = useState(null);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [loading, setLoading]             = useState(false);
  const [loadingPlan, setLoadingPlan]     = useState(null);
  const [error, setError]                 = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const currentPlanKey   = currentPlan.toLowerCase();
  const currentPlanLevel = PLAN_HIERARCHY[currentPlanKey] || 0;

  const subscriptionId = profile?.stripe_subscription_id || null;
  const customerId     = profile?.stripe_customer_id     || null;

  // Direct plan change (upgrade or downgrade) for existing subscribers
  async function handleChangePlan(plan) {
    setLoadingPlan(plan.id);
    setError('');
    setSuccessMessage('');
    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          priceId:        plan.priceId,
          planName:       plan.name,
          subscriptionId: subscriptionId,
          customerId:     customerId,
        }),
      });
      const data = await response.json();
      if (data.error) { setError(data.error); setLoadingPlan(null); return; }
      if (data.type === 'updated') {
        setSuccessMessage(`Your plan has been updated to ${plan.name}. Changes take effect immediately.`);
        if (onPlanChange) onPlanChange();
      } else if (data.url) {
        window.location.href = data.url;
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(`Could not process plan change: ${err.message}`);
    }
    setLoadingPlan(null);
  }

  function handleSelectPlan(plan) {
    setSelected(plan);
    setShowConfirm(true);
    setError('');
    setSuccessMessage('');
  }

  function handleCancel() {
    setShowConfirm(false);
    setSelected(null);
    setError('');
    setLoading(false);
  }

  async function handleCheckout() {
    if (!selected) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          priceId:      selected.priceId,
          setupPriceId: selected.setupPriceId,
          email:        userEmail,
          planName:     selected.name,
          customerId:   customerId,
        }),
      });
      const data = await response.json();
      if (data.error) { setError(data.error); setLoading(false); return; }
      if (!data.url)  { setError('No checkout URL returned. Please try again.'); setLoading(false); return; }
      window.location.href = data.url;
    } catch (err) {
      setError(`Error: ${err.message}`);
      setLoading(false);
    }
  }

  return (
    <div style={{
      height: '100vh', overflowY: 'auto',
      background: 'var(--bg)', fontFamily: 'var(--font)',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 80px' }}>

        {/* ── Success / Error banners ── */}
        {successMessage && (
          <div style={{
            background: 'var(--green-bg)', border: '1px solid var(--green)',
            borderRadius: 8, padding: '12px 16px', fontSize: 13,
            color: 'var(--green)', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>✓</span>
            {successMessage}
          </div>
        )}
        {error && !showConfirm && (
          <div style={{
            background: 'var(--red-bg)', border: '1px solid var(--red)',
            borderRadius: 8, padding: '12px 16px', fontSize: 13,
            color: 'var(--red)', marginBottom: 20,
          }}>
            {error}
          </div>
        )}

        {/* ── Header ── */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 20 }}>
            <LogoMark size={32} />
            <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
              TheClearAudit
            </span>
          </div>

          <h1 style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)', marginBottom: 10, letterSpacing: '-0.02em' }}>
            Get CMMC Certified Before November 2026
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text3)', maxWidth: 520, margin: '0 auto 16px', lineHeight: 1.6 }}>
            The DoD's CMMC Phase 2 deadline is November 2026 — contractors without certification risk losing contracts.
            Start your compliance journey today. All plans include a 7-day free trial.
          </p>

          {/* Current plan badge */}
          {currentPlanKey && PLAN_HIERARCHY[currentPlanKey] && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '6px 16px', borderRadius: 20, marginBottom: 4,
              background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)',
              fontSize: 12, color: 'var(--text2)',
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />
              You are on the <strong style={{ color: 'var(--text)', marginLeft: 3 }}>
                {PLANS.find(p => p.id === currentPlanKey)?.name || currentPlan}
              </strong>&nbsp;plan
            </div>
          )}
        </div>

        {/* ── Value banner ── */}
        <div style={{
          background: 'var(--green-bg)', border: '1px solid rgba(29,184,122,0.3)',
          borderRadius: 8, padding: '10px 20px', marginBottom: 28,
          textAlign: 'center', fontSize: 12, color: 'var(--green)',
        }}>
          November 2026 CMMC deadline approaching — up to 10x more affordable than Vanta, Drata, and Sprinto. Built for defense contractors.
        </div>

        {/* ── Plan Cards ── */}
        <div className="pricing-cards-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20, marginBottom: 32, maxWidth: 760, margin: '0 auto 32px' }}>
          {PLANS.filter(p => p.id !== 'agency').map(plan => {
            const planLevel   = PLAN_HIERARCHY[plan.id] || 1;
            const isCurrent   = plan.id === currentPlanKey;
            const isUpgrade   = planLevel > currentPlanLevel;
            const isDowngrade = currentPlanLevel > 0 && planLevel < currentPlanLevel;

            return (
              <div key={plan.id} style={{
                background: 'var(--bg2)',
                border: `1px solid ${isCurrent ? plan.color : plan.popular ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 12, padding: 20,
                position: 'relative', display: 'flex', flexDirection: 'column',
                transition: 'all 0.2s',
                boxShadow: isCurrent ? `0 0 0 2px ${plan.color}60` : 'none',
              }}>

                {/* Best Value badge */}
                {plan.popular && !isCurrent && (
                  <div style={{
                    position: 'absolute', top: -11, left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--green)', color: '#fff',
                    fontSize: 10, fontWeight: 700,
                    padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>Best Value</div>
                )}

                {/* Current plan badge */}
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: -11, left: '50%',
                    transform: 'translateX(-50%)',
                    background: plan.color, color: '#fff',
                    fontSize: 10, fontWeight: 700,
                    padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>Your Current Plan</div>
                )}

                {/* Plan name */}
                <div style={{ fontSize: 15, fontWeight: 700, color: plan.color, marginBottom: 4 }}>
                  {plan.name}
                </div>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 4 }}>
                  <span style={{ fontSize: 30, fontWeight: 800, color: 'var(--text)' }}>{plan.price}</span>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{plan.period}</span>
                </div>

                {/* Setup fee */}
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  background: 'var(--amber-bg)', border: '1px solid rgba(245,166,35,0.3)',
                  borderRadius: 5, padding: '3px 8px', marginBottom: 12,
                  width: 'fit-content',
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--amber)' }}>{plan.setup}</span>
                  <span style={{ fontSize: 10, color: 'var(--amber)', opacity: 0.8 }}>{plan.setupLabel}</span>
                </div>

                {/* Description */}
                <div style={{
                  fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginBottom: 14,
                }}>
                  {plan.description}
                </div>

                {/* Features */}
                <div style={{ flex: 1, marginBottom: 20 }}>
                  {plan.features.map(feature => (
                    <div key={feature} style={{
                      display: 'flex', alignItems: 'flex-start',
                      gap: 7, fontSize: 11, color: 'var(--text2)', marginBottom: 7,
                    }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%',
                        flexShrink: 0, marginTop: 1,
                        background: 'var(--green-bg)', border: '1px solid var(--green)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 8, fontWeight: 700, color: 'var(--green)',
                      }}>✓</div>
                      {feature}
                    </div>
                  ))}
                </div>

                {/* CTA button */}
                {isCurrent ? (
                  <div style={{
                    width: '100%', padding: '10px', borderRadius: 7,
                    fontSize: 12, fontWeight: 600, textAlign: 'center',
                    border: `1px solid ${plan.color}50`,
                    background: `${plan.color}10`, color: plan.color,
                    boxSizing: 'border-box',
                  }}>
                    Your Current Plan
                  </div>
                ) : isUpgrade ? (
                  <button
                    onClick={() => subscriptionId ? handleChangePlan(plan) : handleSelectPlan(plan)}
                    disabled={loadingPlan === plan.id}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 7,
                      fontSize: 12, fontWeight: 600,
                      cursor: loadingPlan === plan.id ? 'not-allowed' : 'pointer',
                      fontFamily: 'var(--font)', transition: 'all 0.15s',
                      border: `1px solid ${plan.color}`,
                      background: plan.color, color: '#fff',
                      opacity: loadingPlan === plan.id ? 0.7 : 1,
                    }}
                    onMouseEnter={e => { if (loadingPlan !== plan.id) e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={e => { if (loadingPlan !== plan.id) e.currentTarget.style.opacity = '1'; }}
                  >
                    {loadingPlan === plan.id ? 'Updating…' : `Upgrade to ${plan.name}`}
                  </button>
                ) : isDowngrade ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <button
                      onClick={() => subscriptionId ? handleChangePlan(plan) : handleSelectPlan(plan)}
                      disabled={loadingPlan === plan.id}
                      style={{
                        width: '100%', padding: '10px', borderRadius: 7,
                        fontSize: 12, fontWeight: 600,
                        cursor: loadingPlan === plan.id ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--font)', transition: 'all 0.15s',
                        border: '1px solid var(--border2)',
                        background: 'transparent', color: 'var(--text3)',
                        opacity: loadingPlan === plan.id ? 0.7 : 1,
                      }}
                      onMouseEnter={e => { if (loadingPlan !== plan.id) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; } }}
                      onMouseLeave={e => { if (loadingPlan !== plan.id) { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; } }}
                    >
                      {loadingPlan === plan.id ? 'Updating…' : `Switch to ${plan.name}`}
                    </button>
                    <div style={{ fontSize: 9, color: 'var(--text3)', textAlign: 'center', lineHeight: 1.4 }}>
                      Downgrade takes effect at end of billing period
                    </div>
                  </div>
                ) : (
                  /* No current plan set — show Select button */
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 7,
                      fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      fontFamily: 'var(--font)', transition: 'all 0.15s',
                      border: `1px solid ${plan.popular ? 'var(--accent)' : 'var(--border2)'}`,
                      background: plan.popular ? 'var(--accent)' : 'transparent',
                      color: plan.popular ? '#fff' : 'var(--text)',
                    }}
                  >
                    Select {plan.name}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Trust Badges ── */}
        <div className="pricing-trust-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 32 }}>
          {[
            { title: '7-day free trial',   sub: 'No credit card required to start'     },
            { title: 'Cancel anytime',     sub: 'No long-term contracts or commitments' },
            { title: 'Secure payments',    sub: 'Powered by Stripe. 256-bit encryption' },
          ].map(item => (
            <div key={item.title} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '14px 16px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Competitor Comparison ── */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 28,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 14, textAlign: 'center' }}>
            How we compare to the competition
          </div>
          <div className="pricing-comparison-wrap" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
            {[
              { name: 'TheClearAudit',   price: '$399–$799/mo',       focus: 'CMMC specialists',  highlight: true  },
              { name: 'Vanta',      price: '$10,000+/yr',        focus: 'General compliance', highlight: false },
              { name: 'Drata',      price: '$10,000+/yr',        focus: 'General compliance', highlight: false },
              { name: 'Sprinto',    price: '$8,000+/yr',         focus: 'General compliance', highlight: false },
              { name: 'AuditBoard', price: '$20,000+/yr',        focus: 'Enterprise only',    highlight: false },
            ].map(c => (
              <div key={c.name} style={{
                background: c.highlight ? 'var(--accent-glow)' : 'var(--surface)',
                border: `1px solid ${c.highlight ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 8, padding: '12px 10px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 5, color: c.highlight ? 'var(--accent)' : 'var(--text)' }}>
                  {c.name}
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: c.highlight ? 'var(--green)' : 'var(--text2)' }}>
                  {c.price}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{c.focus}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Back link ── */}
        {onBack && (
          <div style={{ textAlign: 'center', paddingBottom: 20 }}>
            <span
              onClick={onBack}
              style={{ fontSize: 13, color: 'var(--text3)', cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
            >
              ← Back to dashboard
            </span>
          </div>
        )}

      </div>

      {/* ── Confirm modal ── */}
      {showConfirm && selected && (
        <div
          onClick={e => e.target === e.currentTarget && handleCancel()}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
            zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 12, padding: 28, width: 420, maxWidth: '90vw',
          }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
              Confirm your plan
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              You are selecting the <strong style={{ color: 'var(--text)' }}>{selected.name}</strong> plan.
              Here is what you will be charged today.
            </div>

            <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
              {[
                { label: `${selected.name} — one-time setup fee`, value: selected.setup },
                { label: `${selected.name} — first month`,        value: selected.price },
              ].map((row, i, arr) => (
                <div key={row.label} style={{
                  display: 'flex', justifyContent: 'space-between', fontSize: 13,
                  marginBottom: i < arr.length - 1 ? 10 : 0,
                  paddingBottom: i < arr.length - 1 ? 10 : 0,
                  borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <span style={{ color: 'var(--text2)' }}>{row.label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--text)' }}>{row.value}</span>
                </div>
              ))}
              <div style={{
                display: 'flex', justifyContent: 'space-between', fontSize: 14,
                marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)',
              }}>
                <span style={{ fontWeight: 600, color: 'var(--text)' }}>Due today</span>
                <span style={{ fontWeight: 800, color: 'var(--accent)', fontSize: 16 }}>
                  {TOTALS[selected.name]}
                </span>
              </div>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.6 }}>
              Then {selected.price}/month starting next month.
              Cancel anytime. 7-day money back guarantee.
            </div>

            {error && (
              <div style={{
                background: 'var(--red-bg)', border: '1px solid var(--red)',
                borderRadius: 6, padding: '10px 12px', fontSize: 12,
                color: 'var(--red)', marginBottom: 14,
              }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleCancel}
                style={{
                  flex: 1, padding: '10px', borderRadius: 7, fontSize: 13, fontWeight: 500,
                  cursor: 'pointer', border: '1px solid var(--border2)',
                  background: 'var(--surface)', color: 'var(--text2)', fontFamily: 'var(--font)',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCheckout}
                disabled={loading}
                style={{
                  flex: 2, padding: '10px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  border: '1px solid var(--accent)',
                  background: 'var(--accent)', color: '#fff',
                  fontFamily: 'var(--font)', opacity: loading ? 0.7 : 1, transition: 'opacity 0.15s',
                }}
              >
                {loading ? 'Redirecting…' : 'Confirm and pay'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
